import { Avatar } from "@mui/material";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react"; // Import useCallback
import { BsSearch } from "react-icons/bs";
import { FaChevronDown, FaUser } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { RxHamburgerMenu } from "react-icons/rx";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getProducts } from "../redux/features/ProductSlice";

// Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

export default function SearchBar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false); // State to control dropdown visibility
  const [expandedCategory, setExpandedCategory] = useState(null); // State for expanded category in mobile view
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false); // State for dropdown visibility
  const dropdownRef = useRef(null); // Ref for the dropdown container
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const categories = useSelector((state) => state.maincategory.categories);
  const products = useSelector((state) => state.mainproduct.products); // Fetch products from Redux store
  const me = useSelector((state) => state.login.me);

  const formattedCategories = categories
    ?.filter((cat) => cat?.parent === null)
    ?.map((fCat) => ({
      ...fCat,
      subCategories: categories?.filter((cat) => cat?.parent?.id === fCat?.id),
    }));

  const handleCategoryClick = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null); // Collapse if already expanded
    } else {
      setExpandedCategory(categoryId); // Expand the clicked category
    }
  };

  const handleLinkClick = (url) => {
    navigate(url); // Navigate to the link
    setIsMenuOpen(false); // Close mobile menu
    setExpandedCategory(null); // Reset expanded category
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((value) => {
      if (value) {
        const filters = { name: { $like: value } };
        dispatch(getProducts({ filters }));
        setShowSuggestions(true); // Show suggestions when typing
      } else {
        setShowSuggestions(false); // Hide suggestions when input is empty
      }
    }, 500), // 500ms debounce delay
    [dispatch]
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value); // Update search term immediately
    debouncedSearch(value); // Trigger debounced search
  };

  const handleSuggestionClick = (productName) => {
    setSearchTerm(productName); // Set the search term to the clicked suggestion
    setShowSuggestions(false); // Hide suggestions
    navigate(`/search?q=${productName}`); // Navigate to the search page
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false); // Hide suggestions after a short delay
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Navigate to the search page when Enter is pressed
      navigate(`/search?q=${searchTerm}`);
      setShowSuggestions(false); // Hide suggestions
    }
  };

  const handleLogout = () => {
    // dispatch(logout()); // Dispatch logout action
    navigate("/login"); // Redirect to login page
    setIsProfileDropdownOpen(false); // Close dropdown
  };

  function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
  }

  function stringAvatar(name) {
    return {
      sx: {
        bgcolor: stringToColor(name),
      },
      children: name?.[0],
    };
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false); // Close dropdown
      }
    };

    // Add event listener when the dropdown is open
    if (isProfileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  return (
    <header className="w-full px-4 py-3 bg-white border-b border-gray-100 shadow-sm">
      <div className="flex items-center justify-between mx-auto max-w-7xl">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0"
        >
          <Link to="/" className="block">
            <div className="relative w-32 h-10 sm:w-40">
              <div className="font-bold text-3xl text-[#E31B54]">
                BETTER<sup className="text-xs">®</sup>
              </div>
            </div>
          </Link>
        </motion.div>

        {/* Search Bar - Hidden on mobile, visible on tablet and up */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="relative flex-grow hidden max-w-xl mx-8 md:block"
        >
          <div
            className={`relative rounded-md transition-all duration-200 ${
              searchFocused ? "ring-2 ring-gray-300" : ""
            }`}
          >
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <BsSearch className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="What are you looking for?"
              className="block w-full py-2 pl-10 pr-3 text-sm placeholder-gray-500 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none"
              onFocus={() => {
                setSearchFocused(true);
                if (searchTerm) setShowSuggestions(true); // Show suggestions if search term exists
              }}
              onBlur={handleInputBlur}
              onChange={handleInputChange} // Use the updated handleInputChange
              onKeyDown={handleKeyDown} // Handle Enter key press
              value={searchTerm}
            />
          </div>

          {/* Dropdown for suggestions */}
          {showSuggestions && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
              <ul>
                {products?.length > 0 ? (
                  products?.map((product) => {
                    let minSellingPrice;
                    let minPrice;
                    if (product?.variations?.length > 0) {
                      const minVariation = product?.variations?.reduce(
                        (min, current) => {
                          return current.selling_price < min.selling_price
                            ? current
                            : min;
                        }
                      );
                      minSellingPrice = minVariation?.selling_price;
                      minPrice = minVariation?.price;
                    } else {
                      minSellingPrice = product?.selling_price;
                      minPrice = product?.price;
                    }
                    return (
                      <li
                        key={product.id}
                        className="flex items-center p-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSuggestionClick(product.name)}
                      >
                        {/* Product Image */}
                        <div className="flex-shrink-0 w-12 h-12">
                          <img
                            src={
                              product?.gallery?.[0]?.url || "/placeholder.svg"
                            }
                            alt={product?.name}
                            className="object-cover w-full h-full rounded-md"
                          />
                        </div>
                        {/* Product Details */}
                        <div className="flex-1 ml-4">
                          <h4 className="text-sm font-medium text-gray-900">
                            {product?.name}
                          </h4>
                          <div className="flex items-center mt-1">
                            <span className="text-sm font-bold text-rose-700">
                              Rs. {minSellingPrice}
                            </span>
                            {minPrice !== minSellingPrice && (
                              <span className="ml-2 text-sm text-gray-500 line-through">
                                Rs. {minPrice}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="p-3 text-sm font-medium text-center text-gray-500">
                    No products to show
                  </li>
                )}
                {/* See All Products Link */}
                {products.length > 0 && (
                  <li
                    className="p-3 text-sm font-medium text-center cursor-pointer text-rose-700 hover:bg-gray-50"
                    onClick={() => {
                      navigate(`/search?q=${searchTerm}`);
                      setShowSuggestions(false); // Hide suggestions
                    }}
                  >
                    See All Products
                  </li>
                )}
              </ul>
            </div>
          )}
        </motion.div>

        {/* Navigation - Hidden on mobile, visible on tablet and up */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="items-center hidden space-x-8 md:flex"
        >
          {me ? (
            <div className="relative" ref={dropdownRef}>
              {/* Avatar with Dropdown */}
              <div
                className="flex flex-col items-center text-sm cursor-pointer"
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
              >
                <Avatar {...stringAvatar(me?.fullName)} />
              </div>

              {/* Dropdown Menu */}
              {isProfileDropdownOpen && (
                <motion.div
                  className="absolute right-0 z-50 w-48 mt-2 bg-white border border-gray-200 rounded-md shadow-lg"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <ul>
                    <li>
                      <Link
                        to="/dashboard"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileDropdownOpen(false)}
                      >
                        Dashboard
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="block w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                </motion.div>
              )}
            </div>
          ) : (
            <div className="flex space-x-4">
              <Link to="/login" className="text-base">
                Login
              </Link>
              <Link to="/register" className="text-base">
                Register
              </Link>
            </div>
          )}
          {/* <Link to="/help" className="flex flex-col items-center text-sm">
            <FaCircle className="w-6 h-6 mb-1" />
            <span>Customer help</span>
          </Link> */}
        </motion.div>

        {/* Mobile Menu Button - Visible on mobile only */}
        <div className="flex items-center space-x-4 md:hidden">
          <button
            onClick={() => setIsMenuOpen(true)}
            className="text-gray-500 focus:outline-none"
            aria-label="Open menu"
          >
            <RxHamburgerMenu className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Menu - Slides in from right */}
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: isMenuOpen ? "0%" : "100%" }}
          transition={{ type: "spring", damping: 25 }}
          className="fixed inset-y-0 right-0 z-50 w-full max-w-xs bg-white shadow-xl md:hidden"
        >
          <div className="flex flex-col h-full overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-bold text-2xl text-[#E31B54]">
                BETTER<sup className="text-xs">®</sup>
              </div>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-500 focus:outline-none"
                aria-label="Close menu"
              >
                <MdClose className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4">
              <div className="relative mb-6">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <BsSearch className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="What are you looking for?"
                  className="block w-full py-2 pl-10 pr-3 text-sm placeholder-gray-500 border border-gray-200 rounded-md focus:border-gray-300 focus:outline-none"
                  onFocus={() => {
                    setSearchFocused(true);
                    if (searchTerm) setShowSuggestions(true); // Show suggestions if search term exists
                  }}
                  onBlur={handleInputBlur}
                  onChange={handleInputChange} // Use the updated handleInputChange
                  onKeyDown={handleKeyDown} // Handle Enter key press
                  value={searchTerm}
                />
              </div>
              {/* Dropdown for suggestions in mobile view */}
              {showSuggestions && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
                  <ul>
                    {products.length > 0 ? (
                      products.map((product) => (
                        <li
                          key={product.id}
                          className="flex items-center p-3 cursor-pointer hover:bg-gray-50"
                          onClick={() => handleSuggestionClick(product.name)}
                        >
                          {/* Product Image */}
                          <div className="flex-shrink-0 w-16 h-16">
                            <img
                              src={
                                product.gallery?.[0]?.url || "/placeholder.svg"
                              }
                              alt={product.name}
                              className="object-cover w-full h-full rounded-md"
                            />
                          </div>
                          {/* Product Details */}
                          <div className="flex-1 ml-4">
                            <h4 className="text-sm font-medium text-gray-900">
                              {product.name}
                            </h4>
                            <div className="flex items-center mt-1">
                              <span className="text-sm font-bold text-rose-700">
                                Rs. {product.selling_price}
                              </span>
                              {product.price && (
                                <span className="ml-2 text-sm text-gray-500 line-through">
                                  Rs. {product.price}
                                </span>
                              )}
                            </div>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="p-3 text-sm font-medium text-center text-gray-500">
                        No products to show
                      </li>
                    )}
                    {/* See All Products Link */}
                    {products.length > 0 && (
                      <li
                        className="p-3 text-sm font-medium text-center cursor-pointer text-rose-700 hover:bg-gray-50"
                        onClick={() => {
                          navigate(`/search?q=${searchTerm}`);
                          setShowSuggestions(false); // Hide suggestions
                        }}
                      >
                        See All Products
                      </li>
                    )}
                  </ul>
                </div>
              )}
              <nav className="space-y-6">
                {/* Parent Categories */}
                {formattedCategories?.map((category) => (
                  <div key={category?.id}>
                    <div
                      onClick={() => handleCategoryClick(category?.id)}
                      className="flex items-center justify-between py-2 space-x-3 cursor-pointer"
                    >
                      {/* <span className="text-base font-medium"> */}
                      <Link key={category?.id} to={`/${category?.slug}`}>
                        {category?.name}
                      </Link>
                      {/* </span> */}
                      {category?.subCategories?.length > 0 && <FaChevronDown />}
                    </div>
                    {/* Subcategories */}
                    {expandedCategory === category?.id && (
                      <div className="pl-4 mt-2 space-y-3">
                        {category?.subCategories?.map((subCategory) => (
                          <Link
                            key={subCategory?.id}
                            to={`/${category?.slug}/${subCategory?.slug}`}
                            onClick={() =>
                              handleLinkClick(
                                `/${category?.slug}/${subCategory?.slug}`
                              )
                            }
                            className="block py-1 text-sm text-gray-700 hover:text-rose-700"
                          >
                            {subCategory?.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {/* Additional Links */}
                <Link
                  to="/dashboard"
                  className="flex items-center py-2 space-x-3"
                >
                  <FaUser className="w-5 h-5" />
                  <span className="text-base font-medium">My Account</span>
                </Link>
                <Link to="/help" className="flex items-center py-2 space-x-3">
                  <span className="text-base font-medium">Customer help</span>
                </Link>
              </nav>
            </div>
          </div>
        </motion.div>
      </div>
    </header>
  );
}
